import { useRef, useState, type JSX } from "react";
import { Link } from "@/lib/router-compat";
import { useUserAccess } from "@/hooks/useUserAccess";
import { useUserRole } from "@/hooks/useUserRole";

const NAVY = "#1B2E6B";

type BadgeDef = {
  id: string;
  label: string;
  gradient: [string, string];
  accent: string;
  accentLight: string;
  description: string;
  Icon: (props: { size?: number }) => JSX.Element;
};

const BADGES: BadgeDef[] = [
  {
    id: "dealer",
    label: "Dealer Verificat",
    gradient: ["#1B2E6B", "#0F1F4D"],
    accent: "#F5C842",
    accentLight: "#FDE68A",
    description: "Pentru dealeri verificați oficial",
    Icon: ({ size = 36 }) => (
      <svg width={size} height={size} viewBox="0 0 36 36" fill="none">
        <path d="M18 3L21.5 10.5L30 11.5L24 17.5L25.5 26L18 22L10.5 26L12 17.5L6 11.5L14.5 10.5L18 3Z" fill="#F5C842" stroke="#E5A800" strokeWidth="0.8" />
        <circle cx="18" cy="17" r="5.5" fill="none" stroke="white" strokeWidth="1.5" />
        <path d="M14.5 17L17 19.5L21.5 14.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: "partener",
    label: "Partener Oficial",
    gradient: ["#1D4ED8", "#1B2E6B"],
    accent: "#60A5FA",
    accentLight: "#BFDBFE",
    description: "Pentru parteneri de lungă durată",
    Icon: ({ size = 36 }) => (
      <svg width={size} height={size} viewBox="0 0 36 36" fill="none">
        <path d="M18 4C18 4 6 9 6 18C6 24 11 29 18 32C25 29 30 24 30 18C30 9 18 4 18 4Z" fill="none" stroke="#60A5FA" strokeWidth="1.8" />
        <path d="M13 18L16.5 21.5L23 14" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="18" cy="18" r="2" fill="#60A5FA" opacity="0.3" />
      </svg>
    ),
  },
  {
    id: "gasesti",
    label: "Ne găsești pe",
    gradient: ["#065F46", "#047857"],
    accent: "#34D399",
    accentLight: "#A7F3D0",
    description: "Prezență pe platforma națională",
    Icon: ({ size = 36 }) => (
      <svg width={size} height={size} viewBox="0 0 36 36" fill="none">
        <path d="M18 5C13 5 9 9 9 14C9 21 18 31 18 31C18 31 27 21 27 14C27 9 23 5 18 5Z" fill="none" stroke="#34D399" strokeWidth="1.8" />
        <circle cx="18" cy="14" r="4" fill="none" stroke="white" strokeWidth="1.5" />
        <circle cx="18" cy="14" r="1.5" fill="#34D399" />
      </svg>
    ),
  },
];

const STYLES = [
  { id: "horizontal", label: "Orizontal", desc: "Footer / Sidebar" },
  { id: "compact", label: "Compact", desc: "Header / Mobil" },
  { id: "seal", label: "Sigiliu", desc: "Homepage / Hero" },
];

export default function PartnersBadge() {
  const { user, accessLevel, subscriptionTier, isLoading } = useUserAccess();
  const { isAdmin } = useUserRole();
  // Doar dealerii cu abonament PLĂTIT activ pot descărca (trial NU contează).
  // Adminii au acces implicit.
  const isPaidDealer =
    isAdmin ||
    (accessLevel === "subscribed" &&
      !!subscriptionTier &&
      subscriptionTier.toLowerCase() !== "trial");

  const [badge, setBadge] = useState(BADGES[0]);
  const [style, setStyle] = useState(STYLES[0]);
  const [darkBg, setDarkBg] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);

  const g1 = badge.gradient[0];
  const g2 = badge.gradient[1];

  const HorizontalBadge = () => (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 0, borderRadius: 14, overflow: "hidden", boxShadow: `0 4px 24px ${g1}55, 0 1px 4px rgba(0,0,0,0.2)`, minWidth: 260, height: 68 }}>
      <div style={{ background: `linear-gradient(160deg, ${g1}, ${g2})`, width: 68, height: "100%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, position: "relative" }}>
        <div style={{ position: "absolute", top: 4, left: 4, width: 10, height: 10, borderRadius: "50%", background: badge.accent, opacity: 0.5 }} />
        <div style={{ position: "absolute", bottom: 6, right: 6, width: 6, height: 6, borderRadius: "50%", background: "white", opacity: 0.2 }} />
        <badge.Icon size={34} />
      </div>
      <div style={{ background: "white", flex: 1, height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", padding: "0 16px", borderTop: `3px solid ${badge.accent}` }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: "#0F172A", letterSpacing: 0.3, lineHeight: 1.2 }}>{badge.label}</div>
        <div style={{ fontSize: 10, fontWeight: 600, color: g1, letterSpacing: 1.5, textTransform: "uppercase", marginTop: 3 }}>RO AUTO MARKET</div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4 }}>
          <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#22C55E" }} />
          <div style={{ fontSize: 9, color: "#64748B", fontWeight: 500 }}>Verificat oficial</div>
        </div>
      </div>
    </div>
  );

  const CompactBadge = () => (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 12, background: `linear-gradient(135deg, ${g1} 0%, ${g2} 100%)`, borderRadius: 50, padding: "10px 22px 10px 10px", boxShadow: `0 4px 20px ${g1}66, 0 1px 4px rgba(0,0,0,0.2)`, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "50%", borderRadius: "50px 50px 0 0", background: "rgba(255,255,255,0.05)", pointerEvents: "none" }} />
      <div style={{ width: 40, height: 40, borderRadius: "50%", background: "rgba(255,255,255,0.12)", display: "flex", alignItems: "center", justifyContent: "center", border: `1.5px solid ${badge.accent}66`, flexShrink: 0, position: "relative" }}>
        <badge.Icon size={24} />
      </div>
      <div style={{ position: "relative" }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: "white", letterSpacing: 0.3, lineHeight: 1.2 }}>{badge.label}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 3 }}>
          <div style={{ width: 4, height: 4, borderRadius: "50%", background: badge.accent }} />
          <div style={{ fontSize: 9, fontWeight: 700, color: badge.accentLight, letterSpacing: 1.8, textTransform: "uppercase" }}>RO AUTO MARKET</div>
        </div>
      </div>
    </div>
  );

  const SealBadge = () => (
    <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: 130, height: 130, borderRadius: "50%", background: `conic-gradient(from 0deg, ${g1}, ${g2}, ${g1})`, boxShadow: `0 6px 32px ${g1}77, 0 2px 8px rgba(0,0,0,0.25)`, position: "relative" }}>
      <div style={{ position: "absolute", inset: 6, borderRadius: "50%", border: `2px solid ${badge.accent}55` }} />
      <div style={{ position: "absolute", inset: 14, borderRadius: "50%", border: `1px solid rgba(255,255,255,0.2)` }} />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
        <div key={deg} style={{ position: "absolute", width: 4, height: 4, borderRadius: "50%", background: badge.accent, opacity: 0.7, top: "50%", left: "50%", transform: `rotate(${deg}deg) translateY(-56px) translate(-50%, -50%)` }} />
      ))}
      <div style={{ position: "relative", textAlign: "center", zIndex: 1 }}>
        <badge.Icon size={38} />
        <div style={{ fontSize: 9, fontWeight: 800, color: "white", letterSpacing: 1.2, textTransform: "uppercase", marginTop: 6, lineHeight: 1.3 }}>
          {badge.label.split(" ").map((w, i) => <div key={i}>{w}</div>)}
        </div>
        <div style={{ fontSize: 7, fontWeight: 600, color: badge.accentLight, letterSpacing: 1.5, textTransform: "uppercase", marginTop: 4, opacity: 0.9 }}>RO AUTO MARKET</div>
      </div>
    </div>
  );

  const BadgePreview = style.id === "horizontal" ? HorizontalBadge : style.id === "compact" ? CompactBadge : SealBadge;

  const handleDownload = async () => {
    if (!isPaidDealer) return;
    if (!previewRef.current || downloading) return;
    setDownloading(true);
    try {
      const node = previewRef.current.firstElementChild as HTMLElement;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const w = Math.ceil(rect.width);
      const h = Math.ceil(rect.height);
      const scale = 3;

      // Inline computed styles for accurate rendering
      const clone = node.cloneNode(true) as HTMLElement;
      const inlineStyles = (src: Element, dst: Element) => {
        const s = window.getComputedStyle(src);
        let css = "";
        for (let i = 0; i < s.length; i++) {
          const prop = s.item(i);
          css += `${prop}:${s.getPropertyValue(prop)};`;
        }
        (dst as HTMLElement).setAttribute("style", css);
        const sc = src.children;
        const dc = dst.children;
        for (let i = 0; i < sc.length; i++) inlineStyles(sc[i], dc[i]);
      };
      inlineStyles(node, clone);

      const xml = new XMLSerializer().serializeToString(clone);
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}"><foreignObject width="100%" height="100%"><div xmlns="http://www.w3.org/1999/xhtml">${xml}</div></foreignObject></svg>`;
      const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(blob);

      const img = new Image();
      img.crossOrigin = "anonymous";
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error("img load failed"));
        img.src = url;
      });

      const canvas = document.createElement("canvas");
      canvas.width = w * scale;
      canvas.height = h * scale;
      const ctx = canvas.getContext("2d")!;
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);

      const link = document.createElement("a");
      link.download = `roautomarket-badge-${badge.id}-${style.id}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (e) {
      console.error("Badge download failed", e);
      alert("Nu am putut genera PNG-ul. Încearcă din nou.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div style={{ fontFamily: "Inter, Arial, sans-serif", background: "#F8FAFC", minHeight: "100vh", padding: "24px 16px" }}>
      <div style={{ maxWidth: 480, margin: "0 auto" }}>
        <Link to="/parteneri" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "#64748B", textDecoration: "none", marginBottom: 12 }}>
          ← Înapoi la parteneri
        </Link>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: NAVY, borderRadius: 10, padding: "6px 14px", marginBottom: 12 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#F5C842" }} />
            <span style={{ color: "white", fontSize: 12, fontWeight: 700, letterSpacing: 0.5 }}>RO AUTO MARKET</span>
          </div>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#0F172A" }}>Badge-uri Premium</div>
          <div style={{ fontSize: 12, color: "#64748B", marginTop: 4 }}>Generator pentru parteneri verificați</div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#94A3B8", letterSpacing: 1.5, marginBottom: 10, textTransform: "uppercase" }}>Tip badge</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {BADGES.map((b) => (
              <button key={b.id} onClick={() => setBadge(b)} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", borderRadius: 12, border: badge.id === b.id ? `2px solid ${b.gradient[0]}` : "2px solid #F1F5F9", background: badge.id === b.id ? "#F8FAFC" : "white", cursor: "pointer", textAlign: "left", boxShadow: badge.id === b.id ? `0 2px 12px ${b.gradient[0]}22` : "none" }}>
                <div style={{ width: 46, height: 46, borderRadius: 12, flexShrink: 0, background: `linear-gradient(135deg, ${b.gradient[0]}, ${b.gradient[1]})`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 2px 8px ${b.gradient[0]}44` }}>
                  <b.Icon size={26} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#0F172A" }}>{b.label}</div>
                  <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 2 }}>{b.description}</div>
                </div>
                {badge.id === b.id && (
                  <div style={{ width: 22, height: 22, borderRadius: "50%", background: b.gradient[0], display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5L4 7L8 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#94A3B8", letterSpacing: 1.5, marginBottom: 10, textTransform: "uppercase" }}>Stilul badge-ului</div>
          <div style={{ display: "flex", gap: 8 }}>
            {STYLES.map((s) => (
              <button key={s.id} onClick={() => setStyle(s)} style={{ flex: 1, padding: "12px 8px", borderRadius: 10, border: style.id === s.id ? `2px solid ${NAVY}` : "2px solid #F1F5F9", background: style.id === s.id ? "#EEF2FF" : "white", cursor: "pointer" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: style.id === s.id ? NAVY : "#475569" }}>{s.label}</div>
                <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 2 }}>{s.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#94A3B8", letterSpacing: 1.5, marginBottom: 10, textTransform: "uppercase" }}>Previzualizare</div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
            <button onClick={() => setDarkBg(!darkBg)} style={{ display: "flex", alignItems: "center", gap: 6, background: darkBg ? "#1E293B" : "#F1F5F9", border: "none", borderRadius: 20, padding: "6px 12px", cursor: "pointer" }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: darkBg ? "#F5C842" : "#94A3B8" }} />
              <span style={{ fontSize: 10, fontWeight: 600, color: darkBg ? "#F5C842" : "#64748B" }}>{darkBg ? "Fundal întunecat" : "Fundal deschis"}</span>
            </button>
          </div>
          <div ref={previewRef} style={{ display: "flex", alignItems: "center", justifyContent: "center", background: darkBg ? "#0F172A" : "#E2E8F0", borderRadius: 16, padding: "40px 20px", minHeight: 160 }}>
            <BadgePreview />
          </div>
        </div>

        {isPaidDealer ? (
          <button onClick={handleDownload} disabled={downloading} style={{ width: "100%", padding: "16px", background: `linear-gradient(135deg, ${NAVY}, #2D4A9A)`, color: "white", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: downloading ? "wait" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, boxShadow: `0 4px 20px ${NAVY}55`, opacity: downloading ? 0.7 : 1 }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M9 2V12M9 12L5 8M9 12L13 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M2 14H16" stroke="white" strokeWidth="2" strokeLinecap="round" />
            </svg>
            {downloading ? "Se generează..." : "Descarcă Badge (PNG)"}
          </button>
        ) : (
          <div style={{ width: "100%", padding: "20px 18px", background: "linear-gradient(135deg, #F8FAFC, #EEF2FF)", border: "2px dashed #C7D2FE", borderRadius: 12, textAlign: "center" }}>
            <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 44, height: 44, borderRadius: "50%", background: NAVY, marginBottom: 10 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M6 10V8a6 6 0 1112 0v2" stroke="#F5C842" strokeWidth="2" strokeLinecap="round" />
                <rect x="4" y="10" width="16" height="11" rx="2" fill="#F5C842" />
                <circle cx="12" cy="15" r="1.5" fill={NAVY} />
              </svg>
            </div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#0F172A", marginBottom: 4 }}>
              {isLoading ? "Se verifică accesul…" : !user ? "Doar pentru dealeri abonați" : accessLevel === "logged_unsubscribed" ? "Necesită abonament activ" : "Necesită abonament plătit (nu trial)"}
            </div>
            <div style={{ fontSize: 12, color: "#64748B", lineHeight: 1.5, marginBottom: 14 }}>
              Badge-urile oficiale RO Auto Market sunt disponibile pentru descărcare doar dealerilor cu abonament plătit activ.
            </div>
            {!user ? (
              <Link to="/auth" style={{ display: "inline-block", padding: "10px 22px", background: NAVY, color: "white", borderRadius: 10, fontSize: 13, fontWeight: 700, textDecoration: "none" }}>
                Conectează-te
              </Link>
            ) : (
              <Link to="/dashboard/abonament" style={{ display: "inline-block", padding: "10px 22px", background: NAVY, color: "white", borderRadius: 10, fontSize: 13, fontWeight: 700, textDecoration: "none" }}>
                Vezi abonamentul
              </Link>
            )}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 16 }}>
          {["3 tipuri", "3 stiluri", "2 teme"].map((t) => (
            <div key={t} style={{ fontSize: 10, color: "#94A3B8", fontWeight: 600 }}>• {t}</div>
          ))}
        </div>
      </div>
    </div>
  );
}
